import { logger } from '@/features/help/utils/logger';
import { FormPage } from '../types/forms';
import { AxiosError } from 'axios';

interface ErrorResponse {
  message?: string;
  data?: unknown;
  status?: number;
}

// Purpose: Service for handling form submission logic
export const formSubmissionService = {
  // Purpose: Logs submission attempt
  logSubmissionAttempt: () => {
    logger.debug('FormSubmissionService', 'Form submission attempt initiated');
  },

  // Purpose: Logs validation failure
  logValidationFailure: (currentPage: FormPage) => {
    logger.info('FormSubmissionService', 'Form validation failed', {
      pageId: currentPage.id,
      pageTitle: currentPage.title,
    });
  },

  // Purpose: Logs successful validation
  logSuccessfulValidation: (userId: string) => {
    logger.info(
      'FormSubmissionService',
      'Form validated successfully, proceeding with submission',
      {
        timestamp: new Date().toISOString(),
        userId: userId || 'unknown',
      }
    );
  },

  // Purpose: Logs submission error
  logSubmissionError: (error: unknown) => {
    const axiosError = error as AxiosError;
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    const responseData = axiosError?.response?.data as ErrorResponse;
    const responseStatus = axiosError?.response?.status;

    logger.error('FormSubmissionService', 'Background submission failed', {
      error: errorMessage,
      response: responseData,
      status: responseStatus,
      stack: error instanceof Error ? error.stack : undefined,
      errorDetails: typeof responseData === 'object' ? responseData : { message: responseData },
    });

    // Analyze the error to provide more context
    if (responseStatus === 500) {
      if (responseData?.message?.includes('Missing required data')) {
        // Extract the request body from the error if available
        const requestBody = axiosError?.config?.data;
        let parsedBody = null;

        try {
          if (requestBody && typeof requestBody === 'string') {
            // For FormData, we can't parse it directly
            if (requestBody.includes('Content-Disposition: form-data;')) {
              logger.error('FormSubmissionService', 'FormData content cannot be fully parsed', {
                bodyPreview: requestBody.substring(0, 500) + '...',
              });
            } else {
              parsedBody = JSON.parse(requestBody);
            }
          }
        } catch (e) {
          logger.error('FormSubmissionService', 'Failed to parse request body', {
            error: e,
          });
        }

        logger.error('FormSubmissionService', 'Server reported missing data in submission', {
          details: responseData.message,
          requestHeaders: axiosError?.config?.headers,
          requestBodyType: typeof requestBody,
          parsedBody,
          possibleCauses: [
            'leadId not provided',
            'userId not provided',
            'structure not provided or malformed',
            'currentSigner not provided',
            'file data corrupted',
            'file mappings incorrect',
          ],
          recommendation: 'Check that all required fields are included in the form data',
        });
      } else if (responseData?.message?.includes('Invalid file')) {
        logger.error('FormSubmissionService', 'Server reported invalid file data', {
          details: responseData.message,
          recommendation: 'Check file format and try uploading again',
        });
      }
    } else if (responseStatus === 400) {
      logger.error('FormSubmissionService', 'Server reported bad request', {
        details: responseData?.message || 'No details provided',
        recommendation: 'Check form data format and required fields',
      });
    } else if (responseStatus === 401 || responseStatus === 403) {
      logger.error('FormSubmissionService', 'Authentication or authorization error', {
        details: responseData?.message || 'No details provided',
        recommendation: 'User may need to log in again',
      });
    }
  },

  // Purpose: Logs validation error
  logValidationError: (error: unknown) => {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    logger.error('FormSubmissionService', 'Validation process error', {
      error: errorMessage,
      stack: error instanceof Error ? error.stack : undefined,
    });
  },

  // Purpose: Creates a delay to allow the submission request to start
  createSubmissionDelay: async (ms: number = 500) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  },
};
